import { type Bid, type BidStatus } from '../bidStore';

/**
 * The bid lifecycle, in the header (plans/todo/bids-status-edit-lock.md, approved 2026-08-07):
 * forward transitions are primary buttons (Submitted offers the Won/Lost pair); the single
 * backward step is a quiet action — and when the current status LOCKS pricing, the page puts a
 * confirm in front of it, because demotion is the consent that reopens editing.
 *
 * A converted bid renders settled: the server refuses any transition ("delete the project
 * first releases it"), and a button that exists only to be rejected is worse than none —
 * the Convert button's own rule.
 */

/** Pricing is a record in these statuses — the server refuses every pricing write (409). */
export const LOCKED_BID_STATUSES: BidStatus[] = ['Submitted', 'Won', 'Lost'];

const FORWARD_STEPS: Partial<Record<BidStatus, BidStatus[]>> = {
  Draft: ['In Review'],
  'In Review': ['Submitted'],
  Submitted: ['Won', 'Lost'],
};

/** One step back per status — the stepper structures the flow; the server stays free. */
export const BACKWARD_STEP: Partial<Record<BidStatus, BidStatus>> = {
  'In Review': 'Draft',
  Submitted: 'In Review',
  Won: 'Submitted',
  Lost: 'Submitted',
};

interface StatusStepperProps {
  bid: Bid;
  /** A forward transition — direct (it only ever locks further, never unlocks). */
  onForward: (next: BidStatus) => void;
  /** The backward step — the page confirms it when it would unlock pricing. */
  onBackward: (next: BidStatus) => void;
}

const StatusStepper = ({ bid, onForward, onBackward }: StatusStepperProps): JSX.Element | null => {
  if (bid.convertedMatterid) {
    return (
      <span className="bid-status-stepper-note">
        Converted — status settled. Deleting the project releases it.
      </span>
    );
  }
  const forward = FORWARD_STEPS[bid.status] ?? [];
  const backward = BACKWARD_STEP[bid.status];
  // "Revise" only when the step TRULY unlocks: current status locked AND the target editable.
  // Won/Lost step back to Submitted — still a record, nothing reopens, and the label must not
  // promise otherwise (review: a consent dialog stating a falsehood is worse than none).
  const unlocks = backward !== undefined
    && LOCKED_BID_STATUSES.includes(bid.status)
    && !LOCKED_BID_STATUSES.includes(backward);
  if (forward.length === 0 && !backward) return null;

  return (
    <div className="bid-status-stepper">
      {forward.map((next) => (
        <button
          key={next}
          className="bid-status-advance"
          onClick={() => onForward(next)}
          title={LOCKED_BID_STATUSES.includes(next) && !LOCKED_BID_STATUSES.includes(bid.status)
            ? `Marks the bid ${next} and locks its pricing`
            : `Marks the bid ${next}`}
        >
          Mark {next}
        </button>
      ))}
      {backward && (
        <button
          className="bid-status-revise"
          onClick={() => onBackward(backward)}
          title={unlocks ? 'Reopens pricing for editing' : undefined}
        >
          {unlocks ? `Revise — back to ${backward}` : `Back to ${backward}`}
        </button>
      )}
    </div>
  );
};

export default StatusStepper;
